// /src/pages/LoginPage.js
import React, { useContext, useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { generateTempToken } from '../api/axiosConfig';
import { isValidIdentifier } from '../utils/extractUUID';
import LoginForm from '../components/LoginForm';
import { WebAppContext } from '../contexts/WebAppContext';
import { Box } from '@chakra-ui/react';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/pages/LoginPage.css';

const LoginPage = () => {
    const { uuid } = useParams(); // This could be a UUID or friendly name like "tdcjerseycity"
    const { setWebAppInfo } = useContext(WebAppContext);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [webAppData, setWebAppData] = useState(null);

    useEffect(() => {
        const fetchToken = async () => {
            setLoading(true);
            setError('');
            
            // Store identifier in localStorage
            localStorage.setItem('UUID', uuid);

            try {
                const response = await generateTempToken(uuid);
                sessionStorage.setItem('tempToken', response.data.tempToken);
                setWebAppInfo(response.data.webApp);
                setWebAppData(response.data.webApp);
            } catch (error) {
                console.error('Error fetching temp token:', error);
                
                // Handle different error scenarios
                if (error.response) {
                    if (error.response.status === 404) {
                        setError('This link is not valid or has been deactivated. Please contact support.');
                    } else if (error.response.status === 500) {
                        setError('Server error occurred. Please try again later.');
                    } else {
                        setError('Failed to authenticate. Please check your link and try again.');
                    }
                } else if (error.request) {
                    setError('Unable to connect to server. Please check your internet connection.');
                } else {
                    setError('An unexpected error occurred. Please try again.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchToken();

        return () => {
            setLoading(false);
        };
    }, [uuid, setWebAppInfo]);

    // Validate identifier (UUID or friendly name) - redirect if invalid or missing
    // This must come AFTER all hooks
    if (!isValidIdentifier(uuid)) {
        return <Navigate to="/error?message=invalid-access" replace />;
    }

    if (loading) {
        return (
            <Box className="login-loading-container">
                <LoadingSpinner />
            </Box>
        );
    }

    return (
        <Box className="login-page-wrapper">
            {error && <ErrorAlert title="Authentication Error" description={error} onClose={() => setError('')} />}
            {webAppData && <Header branding={webAppData.branding} />}
            <Box className="login-main-content">
                <LoginForm />
            </Box>
            {webAppData && <Footer contactInfo={webAppData.contactInfo} legalInfo={webAppData.legalInfo} />}
        </Box>
    );
};

export default LoginPage;